import { Grid, TextField, Tooltip } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import icons from 'constants/icons';
import { isDataEqual } from 'helpers/arrays';
import useBobData from 'hooks/useBobData';
import useDebounce from 'hooks/useDebounce';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BobTable from './BobTable/BobTable';
import BobTableFilters from './BobTableFilters/BobTableFilters';
import RiskManagementTable from './Components/RiskManagementTable';
import style from './DashboardCustomerBob.module.scss';

const SearchIcon = icons['search'];
const ArrowRight = icons['arrowRight'];
const InfoIcon = icons['info'];

const DashboardBOB = ({ setLocalLoader, riskManagement = false }) => {
  // State
  const [showFilters, setShowFilters] = React.useState(false);
  const [filterCount, setFilterCount] = useState(1);
  const [activePanel, setActivePanel] = useState({});
  const [isSearching, setIsSearching] = useState(true);
  const [selectedFilterName, setSelectedFilterName] = useState('');

  // Helper function to format date as MM/DD/YYYY
  const formatDate = date => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Calculate date ranges for Current and Past periods
  const getPeriodOptions = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const isBeforeNov = now.getMonth() < 10; // 10 = November

    // Si estamos antes de noviembre, los períodos son del año anterior
    const currentYear = isBeforeNov ? year - 1 : year;
    const pastYear = currentYear - 1;

    const currentStart = new Date(currentYear, 10, 1);
    const currentEnd = now;

    const pastStart = new Date(pastYear, 10, 1);
    const pastEnd = new Date(currentYear, 9, 31); // October 31

    return [
      `Current (${formatDate(currentStart)} - ${formatDate(currentEnd)})`,
      `Past (${formatDate(pastStart)} - ${formatDate(pastEnd)})`
    ];
  }, []);

  // Refs
  const inputRef = useRef(null);

  const {
    data_for_table,
    bob_records_count,
    currentPage,
    setCurrentPage,
    setCurrentPageSize,
    formData,
    clearFilters,
    handleChangeSelectValue,
    handleNetPremiumFilterChange,
    handleNetPremiumValueChange,
    handleEffectiveDateChange,
    applyFilters,
    setformData,
    setTriggerFetch,
    carriersOptions,
    lastUpdateDate,
    selectedFilterData,
    setSelectedFilterData,
    retentionRateData,
    retentionRateYear,
    setRetentionRateYear
  } = useBobData({
    setLocalLoader,
    setFilterCount,
    setShowFilters,
    setActivePanel,
    riskManagement
  });

  const hasUnsavedFilters = useMemo(
    () => formData && !isDataEqual(selectedFilterData, formData),
    [formData, selectedFilterData]
  );

  const debouncedSearch = useDebounce(formData.searchTerm, 800);

  useEffect(() => {
    if (isSearching) return;
    applyFilters();
    setIsSearching(true);
  }, [debouncedSearch]);

  useEffect(() => {
    setIsSearching(false);
  }, [data_for_table]);

  useEffect(() => {
    if (inputRef.current) {
      if (!isSearching) inputRef.current.focus();
      else inputRef.current.blur();
    }
  }, [isSearching]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100%'
      }}
    >
      <Grid
        container
        columnSpacing={{ xs: 1 }}
        rowSpacing={{ xs: 1 }}
        marginTop={2}
        paddingTop={1}
      >
        <Grid
          item
          xs={showFilters ? 12 : 0}
          lg={showFilters ? 4 : 0}
          xl={showFilters ? 3 : 0}
          className={style.filtersContainerMain}
        >
          <BobTableFilters
            filterCount={filterCount}
            setFilterCount={setFilterCount}
            carriersOptions={carriersOptions}
            formData={formData}
            setformData={setformData}
            clearFilters={clearFilters}
            handleChangeSelectValue={handleChangeSelectValue}
            handleNetPremiumFilterChange={handleNetPremiumFilterChange}
            handleNetPremiumValueChange={handleNetPremiumValueChange}
            handleEffectiveDateChange={handleEffectiveDateChange}
            applyFilters={applyFilters}
            showFilters={showFilters}
            setSelectedFilterData={setSelectedFilterData}
            hasUnsavedFilters={hasUnsavedFilters}
            setSelectedFilterName={setSelectedFilterName}
            riskManagement={riskManagement}
          />
        </Grid>
        <Grid item xs={12} lg={showFilters ? 8 : 12} xl={showFilters ? 9 : 12}>
          <div className={style.tableContainer}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 15px 0px',
                fontSize: '14px',
                fontStyle: 'italic'
              }}
            >
              {riskManagement && (
                <p
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: 600,
                    fontStyle: 'normal'
                  }}
                >
                  Book of Business At Risk
                </p>
              )}
              {selectedFilterName && (
                <p style={{ margin: 0 }}>
                  Selected Filter: {selectedFilterName}
                </p>
              )}
              {lastUpdateDate && (
                <p style={{ margin: 0, marginLeft: 'auto' }}>
                  Last BOB update date: {lastUpdateDate}
                </p>
              )}
            </div>
            <Grid container padding={2} rowSpacing={{ xs: 1 }}>
              <Grid item xs={12} sm={4}>
                <Button
                  text={`${
                    showFilters ? 'Hide Filters' : 'Show Filters'
                  } ${`(${filterCount})`}`}
                  size='medium'
                  className={style.buttonNoWrap}
                  variant='primaryDarkened'
                  icon={
                    <ArrowRight
                      className={
                        showFilters
                          ? style.arrowRightTransitionActive
                          : style.arrowRightTransition
                      }
                    />
                  }
                  onClick={() => {
                    setShowFilters(!showFilters);
                    if (showFilters && hasUnsavedFilters) {
                      applyFilters();
                      setSelectedFilterData(formData);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8} display='flex'>
                <TextField
                  inputRef={inputRef}
                  disabled={isSearching}
                  fullWidth
                  size='small'
                  id='outlined-search'
                  label='Search by holder name, policy number, phone, or email'
                  placeholder='Enter holder name, policy number, phone, or email...'
                  value={formData.searchTerm}
                  onChange={e => {
                    if (!isSearching) {
                      setformData({
                        ...formData,
                        searchTerm: e.target.value
                      });
                    }
                  }}
                  type='search'
                  InputProps={{
                    sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                  }}
                />
                <Button
                  text='Search'
                  variant='primary'
                  size='medium'
                  className={style.searchButton}
                  onClick={() => applyFilters()}
                  icon={<SearchIcon />}
                />
              </Grid>
            </Grid>
            <BobTable
              data_for_table={data_for_table}
              bob_records_count={bob_records_count}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setCurrentPageSize={setCurrentPageSize}
              formData={formData}
              activePanel={activePanel}
              carriersOptions={carriersOptions}
              setTriggerFetch={setTriggerFetch}
              setActivePanel={setActivePanel}
              handleChangeSelectValue={handleChangeSelectValue}
              applyFilters={applyFilters}
              selectedFilterData={selectedFilterData}
              lastUpdateDate={lastUpdateDate}
              riskManagement={riskManagement}
            />
          </div>
        </Grid>
        {riskManagement && (
          <Grid item xs={12}>
            <div className={style.tableContainer}>
              <Grid
                container
                padding={2}
                paddingBottom={1}
                rowSpacing={{ xs: 1 }}
              >
                <Grid item xs={12}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '10px',
                      marginLeft: '10px',
                      marginBottom: '0px',
                      gap: '8px'
                    }}
                  >
                    <label
                      style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        margin: 0
                      }}
                    >
                      Retention Rate By Agent
                    </label>
                    <Tooltip
                      title={
                        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <div
                            style={{ fontWeight: 'bold', marginBottom: '8px' }}
                          >
                            Retention Rate By Agent
                          </div>
                          <div>
                            This table displays the retention rate for each
                            agent in the book of business. The retention rate is
                            calculated as the percentage of clients who remain
                            active compared to the total number of clients.
                            Higher retention rates indicate stronger client
                            retention and greater business stability. A client
                            is only included if their policy&apos;s Effective
                            Date falls within the selected date range.
                          </div>
                        </div>
                      }
                      arrow
                      placement='top'
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#656464',
                          width: '18px',
                          height: '18px'
                        }}
                      >
                        <InfoIcon />
                      </div>
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>
              <RiskManagementTable
                data_for_table={retentionRateData}
                periodOptions={getPeriodOptions}
                retentionRateYear={retentionRateYear}
                setRetentionRateYear={setRetentionRateYear}
              />
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

DashboardBOB.propTypes = {
  setLocalLoader: PropTypes.func,
  riskManagement: PropTypes.bool
};

export default DashboardBOB;
